// Remotion Podcast Video Composition
// Professional podcast video with animated speakers, waveform, and MEDD branding
// Replaces HeyGen for instant, reliable video generation

import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Audio,
  Sequence,
} from "remotion";
import { SpeakerAvatar } from "./SpeakerAvatar";
import { AudioVisualizer } from "./AudioVisualizer";
import { PodcastTitle } from "./PodcastTitle";

// ─── MEDD Brand Colors ────────────────────────────────────────────
const MEDD_PRIMARY = "#0D4A3E";
const MEDD_SECONDARY = "#1A6B5C";
const MEDD_ACCENT = "#2DD4BF";
const MEDD_DARK = "#0A3830";

const HOST_COLORS = [
  { primary: "#3B82F6", glow: "#60A5FA", bg: "#1E3A5F" }, // Blue for host1
  { primary: "#10B981", glow: "#34D399", bg: "#1A4D3E" }, // Emerald for host2
];

// ─── Types ────────────────────────────────────────────────────────

export interface PodcastSegmentData {
  speaker: "host1" | "host2";
  speakerName: string;
  text: string;
  hostIndex: number;
  /** Start time in seconds within the audio */
  startTime: number;
  /** End time in seconds within the audio */
  endTime: number;
}

export interface PodcastCompositionProps {
  title: string;
  description?: string;
  host1Name: string;
  host2Name: string;
  host1AvatarUrl?: string;
  host2AvatarUrl?: string;
  segments: PodcastSegmentData[];
  audioUrl?: string;
  /** Total duration in seconds */
  totalDurationSec: number;
}

// ─── Helper: which segment is active at a given time ──────────────

function getActiveSegmentIndex(
  segments: PodcastSegmentData[],
  timeSec: number
): number {
  for (let i = segments.length - 1; i >= 0; i--) {
    if (timeSec >= segments[i].startTime) return i;
  }
  return 0;
}

// ─── Progress Bar ─────────────────────────────────────────────────

const ProgressBar: React.FC<{ progress: number }> = ({ progress }) => (
  <div
    style={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: 4,
      backgroundColor: "rgba(255,255,255,0.1)",
    }}
  >
    <div
      style={{
        height: "100%",
        width: `${Math.min(100, progress * 100)}%`,
        background: `linear-gradient(90deg, ${MEDD_ACCENT}, ${HOST_COLORS[0].primary})`,
        borderRadius: 2,
        transition: "width 0.1s linear",
      }}
    />
  </div>
);

// ─── Background ───────────────────────────────────────────────────

const AnimatedBackground: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const time = frame / fps;

  return (
    <AbsoluteFill>
      {/* Base gradient */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(135deg, ${MEDD_DARK} 0%, ${MEDD_PRIMARY} 40%, ${MEDD_SECONDARY} 100%)`,
        }}
      />
      {/* Subtle animated circles */}
      <div
        style={{
          position: "absolute",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${MEDD_ACCENT}08 0%, transparent 70%)`,
          top: -200,
          right: -200,
          transform: `scale(${1 + Math.sin(time * 0.3) * 0.1})`,
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${HOST_COLORS[0].primary}08 0%, transparent 70%)`,
          bottom: -100,
          left: -100,
          transform: `scale(${1 + Math.cos(time * 0.25) * 0.1})`,
        }}
      />
      {/* Grid pattern overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
      />
    </AbsoluteFill>
  );
};

// ─── MEDD Logo ────────────────────────────────────────────────────

const MEDDLogo: React.FC = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    }}
  >
    <span
      style={{
        color: "white",
        fontSize: 28,
        fontWeight: 800,
        letterSpacing: 3,
        textTransform: "uppercase",
      }}
    >
      medd.
    </span>
    <span
      style={{
        color: MEDD_ACCENT,
        fontSize: 9,
        fontWeight: 500,
        letterSpacing: 2,
        textTransform: "uppercase",
        marginTop: -2,
      }}
    >
      Simulation Platform
    </span>
  </div>
);

// ─── Transcript Text ──────────────────────────────────────────────

const TranscriptText: React.FC<{
  text: string;
  frame: number;
  fps: number;
  segmentFrame: number;
}> = ({ text, frame, fps, segmentFrame }) => {
  const fadeIn = spring({
    frame: segmentFrame,
    fps,
    config: { damping: 20, stiffness: 100 },
  });

  // Truncate long text
  const displayText = text.length > 200 ? text.slice(0, 197) + "..." : text;

  return (
    <div
      style={{
        color: "rgba(255,255,255,0.85)",
        fontSize: 22,
        lineHeight: 1.6,
        textAlign: "center",
        maxWidth: 800,
        opacity: fadeIn,
        transform: `translateY(${interpolate(fadeIn, [0, 1], [20, 0])}px)`,
        fontWeight: 400,
        letterSpacing: 0.3,
      }}
    >
      {displayText}
    </div>
  );
};

// ─── Main Composition ─────────────────────────────────────────────

export const PodcastComposition: React.FC<PodcastCompositionProps> = ({
  title,
  description,
  host1Name,
  host2Name,
  host1AvatarUrl,
  host2AvatarUrl,
  segments,
  audioUrl,
  totalDurationSec,
}) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const currentTimeSec = frame / fps;

  const activeIndex = getActiveSegmentIndex(segments, currentTimeSec);
  const activeSegment = segments[activeIndex] || segments[0];
  const activeHostIndex = activeSegment?.hostIndex ?? 0;

  // Calculate how many frames into the current segment we are
  const segmentStartFrame = Math.round((activeSegment?.startTime ?? 0) * fps);
  const segmentFrame = Math.max(0, frame - segmentStartFrame);

  const progress = frame / durationInFrames;

  // Title card: first 3 seconds
  const showTitle = currentTimeSec < 3;
  const titleOpacity = showTitle
    ? spring({ frame, fps, config: { damping: 20 } })
    : interpolate(currentTimeSec, [2.5, 3], [1, 0], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });

  return (
    <AbsoluteFill
      style={{ fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}
    >
      {/* Background */}
      <AnimatedBackground />

      {/* Audio */}
      {audioUrl && <Audio src={audioUrl} />}

      {/* Title Card Overlay */}
      {currentTimeSec < 4 && (
        <Sequence from={0} durationInFrames={Math.round(4 * fps)}>
          <PodcastTitle
            title={title}
            description={description}
            host1Name={host1Name}
            host2Name={host2Name}
          />
        </Sequence>
      )}

      {/* Main Content (fades in after title) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          opacity: interpolate(currentTimeSec, [2.5, 3.5], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          }),
        }}
      >
        {/* Top bar: Logo + Episode Title */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "24px 40px",
          }}
        >
          <MEDDLogo />
          <div
            style={{
              color: "rgba(255,255,255,0.6)",
              fontSize: 14,
              fontWeight: 500,
              maxWidth: 400,
              textAlign: "right",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </div>
        </div>

        {/* Center: Speakers + Visualizer */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 32,
            padding: "0 60px",
          }}
        >
          {/* Speaker Avatars Row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 80,
            }}
          >
            <SpeakerAvatar
              name={host1Name}
              hostIndex={0}
              isActive={activeHostIndex === 0}
              color={HOST_COLORS[0]}
              avatarUrl={host1AvatarUrl}
            />

            {/* Center Visualizer */}
            <AudioVisualizer
              isActive={true}
              activeHostIndex={activeHostIndex}
              colors={HOST_COLORS}
            />

            <SpeakerAvatar
              name={host2Name}
              hostIndex={1}
              isActive={activeHostIndex === 1}
              color={HOST_COLORS[1]}
              avatarUrl={host2AvatarUrl}
            />
          </div>

          {/* Current Transcript Text */}
          {activeSegment && (
            <TranscriptText
              text={activeSegment.text}
              frame={frame}
              fps={fps}
              segmentFrame={segmentFrame}
            />
          )}
        </div>

        {/* Bottom: Speaker indicator + segment info */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 40px 24px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor:
                  HOST_COLORS[activeHostIndex]?.primary ?? "#fff",
              }}
            />
            <span
              style={{
                color: "rgba(255,255,255,0.6)",
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {activeSegment?.speakerName ?? ""}
            </span>
          </div>
          <span
            style={{
              color: "rgba(255,255,255,0.4)",
              fontSize: 12,
              fontWeight: 400,
            }}
          >
            {activeIndex + 1} / {segments.length}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <ProgressBar progress={progress} />
    </AbsoluteFill>
  );
};

export default PodcastComposition;
